import { RecordStore } from '@deqo/foundation';
import { injectable } from 'inversify';
import React, { useEffect } from 'react';
import styled, { css } from 'styled-components';
import { useObserver, uuid } from '../CodesThatsGottaGoSomewhereElse';
import { LFC } from '../Legacy';
import { useResolve } from './Container';

export const wrapperTemplate = {
    id: '',
    effect: 'none' as WrapperEffect,
    timeout: 0 as number,
};

export type WrapperEffect = 'shake' | 'drop' | 'none';

@injectable()
export class Wrapper extends RecordStore(wrapperTemplate) {

    public setEffect(effect: WrapperEffect, timeout?: number) {
        this.updateState({ id: uuid(), effect, timeout });
    }
}

const Frame = styled.div<{ effect: WrapperEffect }>`

    & > * {
        transition:
            transform 250ms cubic-bezier(.16, 1.17, .47, 1.01),
            opacity 250ms linear;

        ${p => p.effect !== 'drop' ? '' : css`
            transform: scale(0.93) translateZ(0);
            opacity: 0;
            pointer-events: none;
        `}
    }

    ${p => p.effect === 'shake' && css`
        & > *{
            transition: transform 50ms linear,
        }
    `}
`;

const performShake = (timeout: number, elmt: HTMLElement) => {

    const start = performance.now();
    let last = start;
    let flip = 1;

    const tick = () => {
        const now = performance.now();
        const remaining = Math.max(0, timeout - (now - start));
        if ((now - last) >= 50) {
            const t = remaining / timeout;

            const xf = (30 * t);
            const yf = (5 * t);

            const x = xf * flip;
            const y = (Math.random() * yf * 2) - yf;

            // For reasons I cannot remember, we operate on the children...
            for (const c of Array.from(elmt.children)) {
                if (c instanceof HTMLElement) {
                    c.style.transform = `translate(${x}px, ${y}px)`;
                }
            }

            last = now;
            flip *= -1;
        }

        if (!!remaining) {
            requestAnimationFrame(tick);
        }
    };

    requestAnimationFrame(tick);

};

export const WrapperMount: LFC = ({ children }) => {
    const [elmt, setElmt] = React.useState<HTMLElement | null>(null);
    const { id, effect, timeout } = useObserver(useResolve(Wrapper));

    useEffect(() => {
        if (!elmt) return;
        if (effect === 'shake') {
            performShake(timeout, elmt);
        }
    }, [id, effect]);

    return <Frame ref={setElmt} id="wrapper" effect={effect}>
        {children}
    </Frame>;
};
